import React, { useEffect, useMemo, useRef, useState } from "react";

import downArrow from "../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../assets/images/svg/mab-ellipse.svg";
import dropdownBg from "../assets/images/svg/mab-bd-dropdown2.svg";
import NextButton from "./common/next-button";
import StepNumberBadge from "./common/StepNumberBadge";

type TrackGenreDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  onSelectionChange?: (values: string[]) => void;
  initialSelectedGenres?: string[];
  isEditMode?: boolean;
};

type GenreApiItem = {
  _id: string;
  id: number;
  name: string;
  category: string;
  categoryIndex?: number;
  nameIndex?: number;
};

type GenreCategory = {
  id: string;
  name: string;
  subGenres: string[];
  categoryIndex?: number;
};

const TrackGenreDropdown: React.FC<TrackGenreDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  onSelectionChange,
  initialSelectedGenres = [],
  isEditMode = false,
}) => {
  const isOpen = expandedId === id;
  const [search, setSearch] = useState("");
  const [expandedCat, setExpandedCat] = useState<string>("");
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [categories, setCategories] = useState<GenreCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const userClearedRef = useRef(false);
  const lastEmittedRef = useRef<string[]>([]);
  const normalizeGenre = (value: string) => value.trim().toLowerCase();
  const normalizeGenreLoose = (value: string) =>
    value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const normalizeSearchText = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9 ]/g, "");

  // Fetch genres from API and group by category
  useEffect(() => {
    let isMounted = true;
    const fetchGenres = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        const res = await fetch("https://api.pitchplaylists.com/api/genres");
        const data: GenreApiItem[] = await res.json();

        if (!isMounted) return;
        const grouped = data.reduce<Record<string, GenreCategory>>(
          (acc, item) => {
            if (!acc[item.category]) {
              acc[item.category] = {
                id: item.category.toLowerCase().replace(/\s+/g, "-"),
                name: item.category,
                subGenres: [],
                categoryIndex: item.categoryIndex,
              };
            }
            acc[item.category].subGenres.push(item.name);
            return acc;
          },
          {}
        );

        // Sort categories by categoryIndex if provided, else keep insertion order
        const groupedArray = Object.values(grouped).sort((a, b) => {
          if (a.categoryIndex === undefined || b.categoryIndex === undefined)
            return 0;
          return (a.categoryIndex ?? 0) - (b.categoryIndex ?? 0);
        });

        setCategories(groupedArray);
        // Don't expand any category by default - keep collapsed until user clicks
      } catch (err) {
        if (!isMounted) return;
        setHasError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchGenres();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;

    const searchWords = normalizeSearchText(search).split(" ").filter(Boolean);

    if (searchWords.length === 0) return categories;

    const matchesSearch = (value: string) => {
      const valueWords = normalizeSearchText(value).split(" ").filter(Boolean);
      return searchWords.every((word) =>
        valueWords.some((valueWord) => valueWord.includes(word))
      );
    };

    const filtered: GenreCategory[] = [];

    categories.forEach((cat) => {
      // Same word-based matching behavior as SUBMIT_A_SONG_PLAYLIST search.
      const categoryMatches = matchesSearch(cat.name);

      // Filter sub-genres that match the search
      const matchedSubGenres = cat.subGenres.filter((s) => matchesSearch(s));

      // Include category if name matches OR has matching sub-genres
      if (categoryMatches || matchedSubGenres.length > 0) {
        filtered.push({
          ...cat,
          subGenres: categoryMatches ? cat.subGenres : matchedSubGenres,
        });
      }
    });

    return filtered;
  }, [search, categories]);

  // Emit selected values to parent when user selection (checked) changes.
  // Do NOT include initialSelectedGenres in deps: it comes from parent state; including it causes
  // infinite loop (emit -> parent updates -> new initialSelectedGenres ref -> effect runs again).
  useEffect(() => {
    if (isEditMode) return;
    if (!onSelectionChange) return;
    if (initialSelectedGenres.length > 0 && Object.keys(checked).length === 0) {
      return;
    }
    const selected = Object.entries(checked)
      .filter(([, val]) => val)
      .map(([key]) => key.split("|")[1] || key);

    // Only call onSelectionChange if values actually changed from what we last emitted
    const lastEmitted = lastEmittedRef.current;
    const hasChanged =
      selected.length !== lastEmitted.length ||
      selected.some((val, idx) => val !== lastEmitted[idx]);

    if (hasChanged) {
      onSelectionChange(selected);
      lastEmittedRef.current = selected;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialSelectedGenres intentionally omitted
  }, [checked, onSelectionChange, isEditMode]);

  // Sync initial/controlled selected genres from parent (used in edit/draft restore).
  // Only apply when checked is empty (initial load) or in edit mode – never overwrite user's current selection.
  useEffect(() => {
    if (!categories.length) return;
    if (initialSelectedGenres.length === 0) {
      setChecked({});
      userClearedRef.current = false;
      return;
    }
    if (userClearedRef.current) {
      setChecked({});
      return;
    }
    const firstGenre = initialSelectedGenres[0];
    const normalizedFirstGenre = normalizeGenre(firstGenre);
    const normalizedFirstGenreLoose = normalizeGenreLoose(firstGenre);

    let matchedCategory: GenreCategory | undefined;
    let matchedGenre = firstGenre;

    // 1) Exact subgenre match
    matchedCategory = categories.find((cat) =>
      cat.subGenres.some(
        (genreName) => normalizeGenre(genreName) === normalizedFirstGenre,
      ),
    );
    if (matchedCategory) {
      matchedGenre =
        matchedCategory.subGenres.find(
          (genreName) => normalizeGenre(genreName) === normalizedFirstGenre,
        ) || firstGenre;
    }

    // 2) Loose subgenre match (ignores spaces, dashes, symbols)
    if (!matchedCategory) {
      matchedCategory = categories.find((cat) =>
        cat.subGenres.some((genreName) => {
          const normalizedGenreLoose = normalizeGenreLoose(genreName);
          return (
            normalizedGenreLoose === normalizedFirstGenreLoose ||
            normalizedGenreLoose.includes(normalizedFirstGenreLoose) ||
            normalizedFirstGenreLoose.includes(normalizedGenreLoose)
          );
        }),
      );
      if (matchedCategory) {
        matchedGenre =
          matchedCategory.subGenres.find((genreName) => {
            const normalizedGenreLoose = normalizeGenreLoose(genreName);
            return (
              normalizedGenreLoose === normalizedFirstGenreLoose ||
              normalizedGenreLoose.includes(normalizedFirstGenreLoose) ||
              normalizedFirstGenreLoose.includes(normalizedGenreLoose)
            );
          }) || firstGenre;
      }
    }

    // 3) Category-level match fallback
    if (!matchedCategory) {
      matchedCategory = categories.find((cat) => {
        const normalizedCategoryLoose = normalizeGenreLoose(cat.name);
        return (
          normalizedCategoryLoose === normalizedFirstGenreLoose ||
          normalizedCategoryLoose.includes(normalizedFirstGenreLoose) ||
          normalizedFirstGenreLoose.includes(normalizedCategoryLoose)
        );
      });
    }

    if (!matchedCategory) return;
    const key = `${matchedCategory.id}|${matchedGenre}`;
    setChecked((prev) => {
      if (isEditMode) return { [key]: true };
      // Don't overwrite user's current selection – only apply when empty (initial load / draft restore)
      if (Object.keys(prev).length > 0) return prev;
      if (prev[key] && Object.keys(prev).length === 1) return prev;
      return { [key]: true };
    });
    setExpandedCat(matchedCategory.id);
  }, [initialSelectedGenres, categories, isEditMode]);

  const toggleCheck = (catId: string, name: string) => {
    const key = `${catId}|${name}`;
    setChecked((prev) => {
      // If clicking the already selected item, do nothing – remove only via X icon
      if (prev[key]) {
        return prev;
      }
      // Otherwise, clear all and select only this one (single selection)
      userClearedRef.current = false;
      return { [key]: true };
    });
    // Keep the selected genre's category expanded so the checkmark is visible
    setExpandedCat(catId);
  };

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <StepNumberBadge stepNumber={3} isOpen={isOpen} />
            <span
              className="text-[16px] lg:text-[20px] whitespace-nowrap"
              style={{
                fontWeight: 400,
                lineHeight: "130%",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Track Genre
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="flex flex-col gap-3 sm:gap-4 md:gap-6 px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6 md:ml-[41px]">
            {isLoading && (
              <span className="text-[#8FA5BF] text-sm ">
                Loading genres...
              </span>
            )}
            {hasError && (
              <span className="text-[#EE6161] text-sm font-[Poppins]">
                Failed to load genres. Please try again.
              </span>
            )}
            <span
              className="text-[#8FA5BF] text-[14px] lg:text-[16px]  leading-[130%]"
            >
              Select genre of your song
            </span>

            <div className="relative w-full">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search genre"
                disabled={isEditMode}
                className="w-full text-white placeholder:text-[#8FA5BF] rounded-[12px] h-[45px] md:h-[48px] pl-11 pr-3 outline-none border border-transparent focus:ring-2 focus:ring-[#58F2A9] bg-[#1F2733]"
              />
              <span className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-[#8FA5BF] pointer-events-none">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 9.71015 14.9276 11.2866 13.964 12.5483L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3466 19.0676 17.7794 19.0953 17.3871 18.7903L17.2929 18.7071L12.5483 13.964C11.2866 14.9276 9.71015 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5ZM8 2.5C4.96243 2.5 2.5 4.96243 2.5 8C2.5 11.0376 4.96243 13.5 8 13.5C11.0376 13.5 13.5 11.0376 13.5 8C13.5 4.96243 11.0376 2.5 8 2.5Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </div>

            {/* Selected Genre Tag – whole pill is clickable to remove (genre text + X icon) */}
            {(() => {
              const selectedKey = Object.keys(checked).find(key => checked[key]);
              if (!selectedKey) return null;
              
              const [, genreName] = selectedKey.split("|");
              const handleRemoveGenre = () => {
                if (isEditMode) return;
                userClearedRef.current = true;
                setChecked({});
              };
              return (
                <div className="flex items-center gap-2">
                  <div
                    role="button"
                    tabIndex={0}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1F2733] border border-[#2A2F3D] cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveGenre();
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRemoveGenre();
                      }
                    }}
                    aria-label={`Remove selected genre ${genreName}`}
                  >
                    <span className="text-white text-[14px] [font-family:'Poppins',Helvetica] select-none">
                      {genreName}
                    </span>
                    <span
                      className="flex items-center justify-center w-8 h-8 min-w-[32px] min-h-[32px] rounded-full shrink-0 text-white hover:bg-white/10 transition-colors pointer-events-none"
                      aria-hidden
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 3L3 9M3 3L9 9"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              );
            })()}

            <div
              className="rounded-[12px] md:rounded-[16px] border border-[#1F2733] max-h-[360px] md:max-h-[480px] overflow-y-auto bg-[#202027]"
              style={{
                backgroundImage: `url(${dropdownBg})`,
                backgroundSize: "100% 100%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {filteredCategories.map((cat) => {
                const isExpanded = expandedCat === cat.id;
                return (
                  <div key={cat.id} className="border-b border-[#2A2F3D] last:border-b-0">
                    <button
                      type="button"
                      onClick={() => {
                        if (isEditMode) return;
                        setExpandedCat(isExpanded ? "" : cat.id);
                      }}
                      className="w-full flex items-center justify-between text-left px-4 py-3 font-[Poppins] text-[14px] leading-[100%]"
                      style={{
                        color: isExpanded ? "#58F2A9" : "#FFFFFF",
                        fontWeight: isExpanded ? 500 : 400,
                      }}
                    >
                      <span>{cat.name}</span>
                      <img
                        src={downArrow}
                        alt="toggle"
                        className="pointer-events-none transition-transform flex-shrink-0"
                        style={{
                          height: "9.5px",
                          width: "auto",
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          filter: isExpanded
                            ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                            : "none",
                          opacity: isExpanded ? 1 : 0.6,
                        }}
                      />
                    </button>
                    {cat.subGenres.length > 0 && isExpanded && (
                      <div className="flex flex-col gap-2 px-4 pb-4 pt-1">
                        {cat.subGenres.map((sg) => {
                          const key = `${cat.id}|${sg}`;
                          const isChecked = !!checked[key];
                          return (
                            <div
                              key={key}
                              role="button"
                              tabIndex={0}
                              className="flex items-center gap-3 text-white text-[14px] font-[Poppins] cursor-pointer py-1 outline-none"
                              onClick={(e) => {
                                e.preventDefault();
                                if (isEditMode) return;
                                toggleCheck(cat.id, sg);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  if (isEditMode) return;
                                  toggleCheck(cat.id, sg);
                                }
                              }}
                              aria-pressed={isChecked}
                              aria-label={`Select genre ${sg}`}
                            >
                              <span
                                className={`inline-flex items-center justify-center h-4 w-4 border rounded-[4px] flex-shrink-0 pointer-events-none ${
                                  isChecked ? "border-[#58F2A9] bg-[#58F2A9]" : "border-[#878DA1] bg-transparent"
                                }`}
                              >
                                {isChecked && (
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="text-white">
                                    <path d="M2 5L4 7L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </span>
                              <span>{sg}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex w-full justify-end">
              <NextButton size="sm" className="w-full md:w-auto" onClick={() => onNext && onNext(id)} />
            </div>
            {isEditMode && (
              <span className="text-[#8FA5BF] text-xs font-[Poppins]">
                Track genre is locked in edit mode.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackGenreDropdown;
